export const FALLBACK_THREAD_TITLE = 'Nova conversa';

export const normalizeThreadTitle = (title?: string | null) => {
  if (!title) return FALLBACK_THREAD_TITLE;

  const normalizedTitle = title.trim().replace(/^"+/, '').replace(/"+$/, '').trim().replace(/\s+/g, ' ');

  return normalizedTitle || FALLBACK_THREAD_TITLE;
};

const PROVISIONAL_TITLE_MAX_LENGTH = 80;
const provisionalTitles = new Map<string, string>();

/**
 * Labels a brand-new thread with its first prompt until the generated title arrives, which the AI
 * API only streams after the whole first response. Kept outside the query cache so a refetch that
 * still returns an untitled thread does not flip the sidebar back to the fallback.
 */
export const rememberProvisionalThreadTitle = (threadId: string, prompt?: string) => {
  const title = normalizeThreadTitle(prompt);
  if (title === FALLBACK_THREAD_TITLE) return;
  provisionalTitles.set(threadId, title.length > PROVISIONAL_TITLE_MAX_LENGTH ? `${title.slice(0, PROVISIONAL_TITLE_MAX_LENGTH - 1).trimEnd()}…` : title);
};

export const resolveThreadTitle = (threadId: string, title?: string | null) => {
  const normalizedTitle = normalizeThreadTitle(title);
  if (normalizedTitle !== FALLBACK_THREAD_TITLE) return normalizedTitle;
  return provisionalTitles.get(threadId) ?? normalizedTitle;
};

export type ThreadTitleUpdate = { threadId: string; title: string };

/**
 * Transient `data-thread-title` part the AI API streams right before `finish` on a thread's first
 * turn. It reaches `useChat`'s `onData` only — never `message.parts`.
 */
export type ThreadTitleDataPart = { type: 'data-thread-title'; data: ThreadTitleUpdate };

export const isThreadTitleDataPart = (part: { type: string; data: unknown }): part is ThreadTitleDataPart => {
  if (part.type !== 'data-thread-title' || typeof part.data !== 'object' || part.data === null) return false;
  const { threadId, title } = part.data as Record<string, unknown>;
  return typeof threadId === 'string' && threadId.length > 0 && typeof title === 'string';
};
